import React, { ReactNode, useEffect, useMemo, useState, useRef } from 'react';
import { Box, BoxProps, Flex, Link, Text } from '@opendoor/bricks/core';
import moment, { Duration } from 'moment-timezone';
import { ListingStates } from './constants/constants';
import {
  getEarliestOpenHouse,
  remainingDurationToEndTime,
} from '../../helpers/exclusives/exclusiveListingInfo';
import AgentBuyModal from './AgentBuyModal';
import { Listing } from '../../declarations/exclusives/listing';
import AccountConnector, { AccountProps } from '../shared/AccountConnector';
import {
  getHasOngoingOrUpcomingOpenHouses,
  getIsExpiringSoon,
  getIsOOMLS,
  getIsPostOpenHouse,
  getIsSellerOfferExperiment,
  getListingStreet,
} from '../../helpers/exclusives/listing';
import OpenHouseTagText from './openHouse/OpenHouseTagText';
import ExclusiveBadge from './ExclusiveBadge';
import HeroSupportSection from './HeroSupportSection';
import HeroPriceAndBenefitsSection from './HeroPriceAndBenefitsSection';
import HeroActions from './HeroActions';
import { getWatchlistButtonDisplayText, WatchlistContext } from './WatchlistButton';
import useWatchlist from './hooks/useWatchlist';
import { NotificationStatus, useNotificationSectionContext } from './NotificationSection';
import { useInView } from 'framer-motion';
import { RequestATourProvider } from './HeroPriceAndBenefitsSection/RequestATour';

// The setup for moment-duration-format should be only made where it's being used
// otherwise we would be sending a bigger JS than we need for some pages.
// eslint-disable-next-line @typescript-eslint/no-var-requires
const momentDurationFormatSetup = require('moment-duration-format');
momentDurationFormatSetup(moment);

interface Props extends AccountProps {
  onTourButtonClick: () => void;
  listing: Listing & { el_state: ListingStates };
  tourableListings: (Listing & { el_state: ListingStates })[];
  isExclusivesRequestATourFFEnabled: boolean;
}

const FINAL_STATES = [ListingStates.EL_SOLD, ListingStates.EL_IN_CONTRACT];

const sectionDefaultSpacing: BoxProps = {
  maxWidth: ['644px', null, null, '942px'],
  width: ['unset', null, 'calc(100% - 3rem)', 'unset'],
  ml: ['1.5rem', null, 'auto', '3.5rem'],
  mr: ['1.5rem', null, 'auto', '2rem'],
};

const Hero: React.FC<Props> = ({
  onTourButtonClick,
  listing,
  tourableListings,
  isExclusivesRequestATourFFEnabled,
  ...account
}) => {
  const initialDetailsRef = useRef<HTMLDivElement>(null);
  const earliestOpenHouse = useMemo(
    () => getEarliestOpenHouse(listing.exclusive_listing_info),
    [listing],
  );
  const earliestOpenHouseTagText = useMemo(
    () =>
      earliestOpenHouse && (
        <OpenHouseTagText openHouse={earliestOpenHouse} timezone={listing.timezone} />
      ),
    [earliestOpenHouse],
  );
  const elState = listing.el_state;
  const isSellerOfferExperiment = useMemo(() => getIsSellerOfferExperiment(listing), [listing]);
  const hasOngoingOrUpcomingOpenHouses = getHasOngoingOrUpcomingOpenHouses(listing);
  // TODO: Pass postOpenHouse up from the backend, for now assume that a short end time window
  // means the open house has passed.
  const expiringSoon = getIsExpiringSoon(listing);
  const postOpenHouse = getIsPostOpenHouse(listing, hasOngoingOrUpcomingOpenHouses, expiringSoon);

  const listingStates: Record<ListingStates, { text: ReactNode; isPrimary?: boolean } | undefined> =
    {
      [ListingStates.EL_LISTED]: {
        text: earliestOpenHouseTagText && !postOpenHouse ? earliestOpenHouseTagText : 'Exclusive',
        isPrimary: !earliestOpenHouseTagText || postOpenHouse,
      },
      [ListingStates.EL_SOLD]: { text: 'Sold' },
      [ListingStates.EL_IN_CONTRACT]: { text: 'In Contract' },
      [ListingStates.EL_RESERVATION_LOCKED]: { text: 'In Contract' }, // Show same as in contract
      [ListingStates.EL_SETUP]: {
        text: ((info) => {
          if (earliestOpenHouseTagText) return earliestOpenHouseTagText;
          if (info.start_time) {
            const formattedDate = moment(info.start_time).format('MMM Do');
            return `This home will be available to buy on ${formattedDate}`;
          }

          return `Available Soon`;
        })(listing.exclusive_listing_info),
      },
      [ListingStates.EL_EXPIRED]: {
        text: 'This home is no longer available via Exclusives',
      },
      [ListingStates.RESERVATION_PENDING]: {
        text: 'A buyer is currently setting up a contract on this home',
      },
      [ListingStates.OOMLS]: undefined,
    };

  const tag = listingStates[elState];

  const infoColumns: { label: string; value: ReactNode }[] = [
    { label: 'Beds', value: listing.bedrooms },
    { label: 'Baths', value: listing.bathrooms },
    { label: 'Size', value: `${listing.square_footage.toLocaleString()} sqft` },
    { label: 'Lot size', value: `${listing.lot_square_footage.toLocaleString()} sqft` },
  ];

  const [durationToEndTime, setDurationToEndTime] = useState<Duration | undefined>(undefined);

  useEffect(() => {
    if (listing.exclusive_listing_info.end_time && !isSellerOfferExperiment) {
      const timeout = setTimeout(
        () => setDurationToEndTime(remainingDurationToEndTime(listing.exclusive_listing_info)),
        1000,
      );
      return () => clearTimeout(timeout);
    }
    return () => null;
  }, [durationToEndTime, listing]);
  const { displayNotification } = useNotificationSectionContext();
  const { isOnWatchlist, setIsOnWatchlist, addToWatchlist, toggleWatchlist, isLoading } =
    useWatchlist(listing, false, {
      onAddedToWatchlist: () =>
        displayNotification(NotificationStatus.Success, 'Subscribed to email updates'),
      onRemovedFromWatchlist: () =>
        displayNotification(NotificationStatus.Info, 'Removed from email updates'),
      onError: () =>
        displayNotification(
          NotificationStatus.Error,
          'Sorry! A error ocurred, please try again later.',
        ),
    });
  const buttonDisplayText = useMemo(
    () => getWatchlistButtonDisplayText(listing, isOnWatchlist),
    [listing.listing_price_cents, isOnWatchlist],
  );

  return (
    <WatchlistContext.Provider
      value={{
        toggleWatchlist,
        isOnWatchlist,
        setIsOnWatchlist,
        addToWatchlist,
        isLoading,
        listing,
        buttonDisplayText,
        source: 'pdp',
      }}
    >
      <RequestATourProvider
        listing={listing}
        tourableListings={tourableListings}
        account={account}
        isExclusivesRequestATourFFEnabled={isExclusivesRequestATourFFEnabled}
      >
        <Box width="100%" py="1.5rem">
          <Box ref={initialDetailsRef} width="100%">
            {tag && (
              <Flex
                {...sectionDefaultSpacing}
                flexDirection="row"
                alignItems="center"
                mb="2rem"
                gridGap="0.875rem"
                flexWrap="wrap"
              >
                {!!tag.text && (
                  <ExclusiveBadge
                    textProps={{
                      as: 'div',
                    }}
                    {...(!tag.isPrimary && {
                      hideIcon: true,
                      color: 'neutrals0',
                      fontWeight: 'semibold',
                      background: '#0A0F1A',
                    })}
                  >
                    {tag.text}
                  </ExclusiveBadge>
                )}

                <Text fontSize="s0" fontWeight="semibold">
                  For sale only on Opendoor Exclusives
                </Text>
              </Flex>
            )}

            <Text
              {...sectionDefaultSpacing}
              as="h1"
              mb="2rem"
              // @ts-expect-error - deprecated color
              color="#0A0F1A"
              fontWeight="bold"
              fontSize={['s3', 's3', 's3', 's4']}
              lineHeight={['s3', 's3', 's3', 's4']}
            >
              {listing.exclusive_listing_info.should_obfuscate_address
                ? `${listing.bedrooms}br, ${listing.bathrooms}ba near `
                : ''}
              {getListingStreet(listing)}
              <br />
              {listing.address.city}
              {', '}
              {listing.address.state} {listing.address.postal_code}
            </Text>
            <Box
              {...sectionDefaultSpacing}
              as="dl"
              pb="1.5rem"
              borderBottom="1px solid"
              borderColor="neutrals40"
              gridTemplateColumns="repeat(4, auto)"
              mb={['1rem', '1rem', '1rem', '2.5rem']}
              display={['grid', 'grid', 'grid', 'flex']}
              gridGap={['1.5rem', '1.5rem', '1.5rem', '1.5rem', '3rem']}
              {...(getIsOOMLS(listing) && { mb: 'unset', borderBottom: 'unset' })}
            >
              {FINAL_STATES.includes(elState) && tag && (
                <Box>
                  <Text color="neutrals90" lineHeight="s000" as="dt">
                    Status
                  </Text>
                  <Text
                    ml={0}
                    as="dd"
                    mt="0.5rem"
                    lineHeight="s000"
                    color="brand50"
                    fontWeight="semibold"
                  >
                    {tag.text}
                  </Text>
                </Box>
              )}

              {infoColumns.map((column) => {
                return (
                  <Flex key={column.label} flexDirection="column">
                    <Text color="neutrals90" lineHeight="s000" as="dt">
                      {column.label}
                    </Text>
                    <Text
                      ml={0}
                      fontSize={['s0', 's0', 's1']}
                      as="dd"
                      mt="0.5rem"
                      lineHeight="s000"
                      color="neutrals100"
                      fontWeight="semibold"
                    >
                      {column.value}
                    </Text>
                  </Flex>
                );
              })}

              <HeroActions
                listing={listing}
                onSharingError={(message) => displayNotification(NotificationStatus.Error, message)}
                onSharingSuccess={(message) =>
                  displayNotification(NotificationStatus.Success, message)
                }
              />
            </Box>
          </Box>

          <HeroPriceAndBenefitsSection
            listing={listing}
            onTourButtonClick={onTourButtonClick}
            additionalConditionToShowBottomBar={!useInView(initialDetailsRef)}
          />
          <HeroSupportSection {...sectionDefaultSpacing} mt="2.5rem" mb="1.5rem" />
          {listing.el_state === ListingStates.EL_LISTED && !isSellerOfferExperiment && (
            <Flex {...sectionDefaultSpacing} pt="1rem">
              <AgentBuyModal listing={listing} />
            </Flex>
          )}
          <Flex {...sectionDefaultSpacing} mt="2.8rem" bg="neutrals40" height="1px" />

          {/* Show prior photo disclaimer if has prior photos */}
          {listing.listing_photos?.[0]?.isPrior && (
            <Flex
              {...sectionDefaultSpacing}
              as="section"
              borderBottom="1px solid"
              borderColor="neutrals40"
              gridGap="0.5rem"
              flexDirection="column"
              py="2.8rem"
            >
              <Text h="h3" fontSize="s3" lineHeight="s3" fontWeight="bold">
                Disclaimer
              </Text>
              <Text as="div" fontSize="s1" lineHeight="s0">
                Photos are from a historical listing in {listing.listing_photos?.[0]?.year} and
                hosted by Opendoor Brokerage.
                <br />
                <Link
                  analyticsName="view-full-historical-listing"
                  aria-label="View full historical listing"
                  href={`/properties/aid_${listing.address.address_token}`}
                  target="_blank"
                >
                  View full historical listing
                </Link>
              </Text>
            </Flex>
          )}
        </Box>
      </RequestATourProvider>
    </WatchlistContext.Provider>
  );
};

export default AccountConnector(Hero);
